import React, { useState, useCallback, useEffect } from "react";
import { loadWeb3, getWeb3Instance } from "./Connectivity/Connectivity";
import { toast } from "react-toastify";
import {
  USDT,
  USDTAbi,
} from "./Contract";
import "bootstrap/dist/css/bootstrap.min.css";

import { pynexnftMatrix, pynexnftMatrixAbi } from './Contract';

export default function Page1() {
  const [btnTxt, setBtnTxt] = useState("Connect Wallet");
  const [account, setAccount] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [loadingStates, setLoadingStates] = useState({});
  
  // Function inputs and outputs storage
  const [functionInputs, setFunctionInputs] = useState({});
  const [functionOutputs, setFunctionOutputs] = useState({});
  
  // Overview data
  const [matrixData, setMatrixData] = useState({
    totalUsers: "",
    totalLevel: "",
    directIncomePercent: "",
    levelIncomePercent: "",
    paused: false,
    usdtBalance: "",
    userUsdtBalance: ""
  });

  // Matrix contract functions definitions - All from ABI
  const matrixFunctions = {
    readFunctions: [
      {
        name: "burnFeeReceiver",
        title: "Burn Fee Receiver",
        description: "Get the burn fee receiver address",
        inputs: [],
        icon: "🔥",
        category: "Basic Info"
      },
      {
        name: "burnIncomePercent",
        title: "Burn Income %",
        description: "Get the burn income percentage",
        inputs: [],
        icon: "🔥",
        category: "Income",
        formatOutput: (value) => `${value}%`
      },
      {
        name: "directIncomePercent",
        title: "Direct Income %",
        description: "Get the direct income percentage",
        inputs: [],
        icon: "💰",
        category: "Income",
        formatOutput: (value) => `${value}%`
      },
      {
        name: "findFreeReferrer",
        title: "Find Free Referrer",
        description: "Find a free referrer",
        inputs: [{ name: "_user", type: "address", placeholder: "Enter user address", required: true, label: "User Address" }],
        icon: "🔍",
        category: "Tree Structure"
      },
      {
        name: "getFundEligible",
        title: "Get Fund Eligible",
        description: "Check if account is fund eligible",
        inputs: [{ name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" }],
        icon: "✅",
        category: "User Info",
        formatOutput: (value) => value ? 'Yes' : 'No'
      },
      {
        name: "getIncomeAmount",
        title: "Get Income Amount",
        description: "Get income amount for level and income type",
        inputs: [
          { name: "level", type: "uint8", placeholder: "Enter level", required: true, label: "Level" },
          { name: "income", type: "uint8", placeholder: "Enter income type", required: true, label: "Income Type" }
        ],
        icon: "💰",
        category: "Income"
      },
      {
        name: "getLevelTreeDetails",
        title: "Get Level Tree Details",
        description: "Get level tree details for user",
        inputs: [{ name: "userAccount", type: "address", placeholder: "Enter user address", required: true, label: "User Address" }],
        icon: "🌲",
        category: "Tree Structure",
        formatOutput: (value) => `First Upline: ${value.firstUpline || value[0]}\nDownlines: ${value.downlines?.join(', ') || value[1].join(', ')}\nIs Downline Reached: ${value.isDownlineReached || value[2] ? 'Yes' : 'No'}`
      },
      {
        name: "getSaleDirectCondition",
        title: "Get Sale Direct Condition",
        description: "Get sale direct condition for tree line",
        inputs: [{ name: "treeLine", type: "uint256", placeholder: "Enter tree line", required: true, label: "Tree Line" }],
        icon: "📊",
        category: "Conditions"
      },
      {
        name: "getSubDirectCondition",
        title: "Get Sub Direct Condition",
        description: "Get sub direct condition for tree line",
        inputs: [{ name: "treeLine", type: "uint256", placeholder: "Enter tree line", required: true, label: "Tree Line" }],
        icon: "📊",
        category: "Conditions"
      },
      {
        name: "getTeamSize",
        title: "Get Team Size",
        description: "Get team size for account with require size",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" },
          { name: "requireSize", type: "uint256", placeholder: "Enter require size", required: true, label: "Require Size" }
        ],
        icon: "👥",
        category: "User Info"
      },
      {
        name: "getTotalRewardChunk",
        title: "Get Total Reward Chunk",
        description: "Get total reward chunk for account and amount",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" },
          { name: "amount", type: "uint256", placeholder: "Enter amount", required: true, label: "Amount" }
        ],
        icon: "💰",
        category: "Rewards"
      },
      {
        name: "getPrincipleBurnAmount",
        title: "Get Principle Burn Amount",
        description: "Get principle burn amount for account and principle amount",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" },
          { name: "principleAmount", type: "uint256", placeholder: "Enter principle amount", required: true, label: "Principle Amount" }
        ],
        icon: "🔥",
        category: "Rewards"
      },
      {
        name: "getUserDownline",
        title: "Get User Downline",
        description: "Get user downline",
        inputs: [{ name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" }],
        icon: "🌳",
        category: "User Info",
        formatOutput: (value) => value.join(', ') || 'None'
      },
      {
        name: "getUserHoldingLimit",
        title: "Get User Holding Limit",
        description: "Get user holding limit",
        inputs: [{ name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" }],
        icon: "📋",
        category: "User Info"
      },
      {
        name: "getUserLevelStatus",
        title: "Get User Level Status",
        description: "Get user level status",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" },
          { name: "level", type: "uint8", placeholder: "Enter level", required: true, label: "Level" }
        ],
        icon: "🎯",
        category: "User Info",
        formatOutput: (value) => value ? 'Activated' : 'Not Activated'
      },
      {
        name: "getUserPlanLimt",
        title: "Get User Plan Limit",
        description: "Get user plan limit",
        inputs: [{ name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" }],
        icon: "📋",
        category: "User Info"
      },
      {
        name: "globalNftLimitPeriod",
        title: "Global NFT Limit Period",
        description: "Get global NFT limit period",
        inputs: [],
        icon: "⏱️",
        category: "Basic Info"
      },
      {
        name: "holdingMax",
        title: "Holding Max",
        description: "Get holding max",
        inputs: [],
        icon: "📈",
        category: "Basic Info"
      },
      {
        name: "idToAddress",
        title: "ID To Address",
        description: "Get address from ID",
        inputs: [{ name: "id", type: "uint256", placeholder: "Enter ID", required: true, label: "ID" }],
        icon: "🆔",
        category: "Mappings"
      },
      {
        name: "isUserExists",
        title: "Is User Exists",
        description: "Check if user exists",
        inputs: [{ name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" }],
        icon: "🔍",
        category: "User Info",
        formatOutput: (value) => value ? 'Yes' : 'No'
      },
      {
        name: "isUserSubscribed",
        title: "Is User Subscribed",
        description: "Check if user is subscribed",
        inputs: [{ name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" }],
        icon: "✅",
        category: "User Info",
        formatOutput: (value) => value ? 'Yes' : 'No'
      },
      {
        name: "isBotStatus",
        title: "Is Bot Status",
        description: "Check if account has bot status",
        inputs: [{ name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" }],
        icon: "🤖",
        category: "User Info",
        formatOutput: (value) => value ? 'Yes' : 'No'
      },
      {
        name: "lastUserAddress",
        title: "Last User Address",
        description: "Get last user address",
        inputs: [],
        icon: "👤",
        category: "User Info"
      },
      {
        name: "levelIncomePercent",
        title: "Level Income %",
        description: "Get level income percent",
        inputs: [],
        icon: "📊",
        category: "Income",
        formatOutput: (value) => `${value}%`
      },
      {
        name: "levelIncomehresholds",
        title: "Level Income Thresholds",
        description: "Get level income threshold",
        inputs: [{ name: "index", type: "uint256", placeholder: "Enter index", required: true, label: "Index" }],
        icon: "📈",
        category: "Income"
      },
      {
        name: "nftMarketPlace",
        title: "NFT Market Place",
        description: "Get NFT market place address",
        inputs: [],
        icon: "🎨",
        category: "Basic Info"
      },
      {
        name: "nftReward",
        title: "NFT Reward",
        description: "Get NFT reward address",
        inputs: [],
        icon: "🏆",
        category: "Basic Info"
      },
      {
        name: "owner",
        title: "Owner",
        description: "Get owner",
        inputs: [],
        icon: "👑",
        category: "Basic Info"
      },
      {
        name: "paused",
        title: "Paused",
        description: "Check if paused",
        inputs: [],
        icon: "⏸️",
        category: "Basic Info",
        formatOutput: (value) => value ? 'Yes' : 'No'
      },
      {
        name: "planInfo",
        title: "Plan Info",
        description: "Get plan info",
        inputs: [{ name: "level", type: "uint8", placeholder: "Enter level", required: true, label: "Level" }],
        icon: "📋",
        category: "Pricing",
        formatOutput: (value) => `Plan Price: ${value.planPrice || value[0]}\nPlan Limit: ${value.planLimit || value[1]}\nHolding NFT: ${value.holdingNft || value[2]}`
      },
      {
        name: "platformIncomePercent",
        title: "Platform Income %",
        description: "Get platform income percent",
        inputs: [],
        icon: "🏢",
        category: "Income",
        formatOutput: (value) => `${value}%`
      },
      {
        name: "platFormFeeTwoPercent",
        title: "Platform Fee Two %",
        description: "Get platform fee two percentage",
        inputs: [],
        icon: "🏢",
        category: "Income",
        formatOutput: (value) => `${value}%`
      },
      {
        name: "platFormFeeTwoReceiver",
        title: "Platform Fee Two Receiver",
        description: "Get platform fee two receiver address",
        inputs: [],
        icon: "📤",
        category: "Basic Info"
      },
      {
        name: "saleNftDirectThresholds",
        title: "Sale NFT Direct Thresholds",
        description: "Get sale NFT direct threshold",
        inputs: [{ name: "index", type: "uint256", placeholder: "Enter index", required: true, label: "Index" }],
        icon: "📊",
        category: "Conditions"
      },
      {
        name: "saleNftLevelPercents",
        title: "Sale NFT Level Percents",
        description: "Get sale NFT level percent",
        inputs: [{ name: "index", type: "uint256", placeholder: "Enter index", required: true, label: "Index" }],
        icon: "📊",
        category: "Income",
        formatOutput: (value) => `${value}%`
      },
      {
        name: "topWallet",
        title: "Top Wallet",
        description: "Get top wallet",
        inputs: [],
        icon: "👛",
        category: "Basic Info"
      },
      {
        name: "totalLevel",
        title: "Total Level",
        description: "Get total level",
        inputs: [],
        icon: "🏆",
        category: "Basic Info"
      },
      {
        name: "totalUsers",
        title: "Total Users",
        description: "Get total users",
        inputs: [],
        icon: "👥",
        category: "Stats"
      },
      {
        name: "usdt",
        title: "USDT",
        description: "Get USDT address",
        inputs: [],
        icon: "💵",
        category: "Basic Info"
      },
      {
        name: "userHoldInfo",
        title: "User Hold Info",
        description: "Get user hold info",
        inputs: [{ name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" }],
        icon: "📋",
        category: "User Info",
        formatOutput: (value) => `User Total Limit: ${value.userTotalLimit || value[0]}\nTotal Reward Earned: ${value.totalRewardEarned || value[1]}\nUser Last Draw: ${value.userLastDraw || value[2]}\nUser Last Cycle: ${value.userLastCycle || value[3]}\nIs Exclude Condition: ${value.isExcludeCondition || value[4] ? 'Yes' : 'No'}\nIs Blocked: ${value.isBlocked || value[5] ? 'Yes' : 'No'}\nIs Resync: ${value.isResync || value[6] ? 'Yes' : 'No'}\nPrinciple Burn Collected: ${value.principleBurnCollected || value[7]}`
      },
      {
        name: "userHoldingAmount",
        title: "User Holding Amount",
        description: "Get user holding amount",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" },
          { name: "level", type: "uint8", placeholder: "Enter level", required: true, label: "Level" }
        ],
        icon: "💰",
        category: "User Info"
      },
      {
        name: "userHoldingLevelAmount",
        title: "User Holding Level Amount",
        description: "Get user holding level amount",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" },
          { name: "index", type: "uint256", placeholder: "Enter index", required: true, label: "Index" }
        ],
        icon: "💰",
        category: "User Info"
      },
      {
        name: "userHoldingStatus",
        title: "User Holding Status",
        description: "Get user holding status",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" },
          { name: "index", type: "uint256", placeholder: "Enter index", required: true, label: "Index" }
        ],
        icon: "📋",
        category: "User Info",
        formatOutput: (value) => value ? 'Yes' : 'No'
      },
      {
        name: "userInfo",
        title: "User Info",
        description: "Get user info",
        inputs: [{ name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" }],
        icon: "👤",
        category: "User Info",
        formatOutput: (value) => `Current Level: ${value.currentLevel || value[0]}\nID: ${value.id || value[1]}\nDirect Count: ${value.directCount || value[2]}\nReward Earned: ${value.rewardEarned || value[3]}\nLast Time Stamp: ${value.lastTimeStamp || value[4]}\nDirect Referrer: ${value.directReferrer || value[5]}\nFirst Upline: ${value.firstUpline || value[6]}\nIs Downline Reached: ${value.isDownlineReached || value[7] ? 'Yes' : 'No'}`
      },
      {
        name: "userLevelHoldRequire",
        title: "User Level Hold Require",
        description: "Get user level hold require",
        inputs: [],
        icon: "📋",
        category: "Conditions"
      }
    ],
    
    writeFunctions: [
      // User Actions
      {
        name: "subscribe",
        title: "Subscribe",
        description: "Subscribe with referrer and level",
        inputs: [
          { name: "referrer", type: "address", placeholder: "Enter referrer address", required: true, label: "Referrer Address" },
          { name: "level", type: "uint8", placeholder: "Enter level", required: true, label: "Level" }
        ],
        requiresApproval: true,
        requiresBalanceCheck: true,
        icon: "🚀",
        category: "User Actions"
      },
      {
        name: "nftSaleDistribute",
        title: "NFT Sale Distribute",
        description: "Distribute NFT sale",
        inputs: [
          { name: "tokenId", type: "uint256", placeholder: "Enter token ID", required: true, label: "Token ID" },
          { name: "currentDirect", type: "address", placeholder: "Enter current direct", required: true, label: "Current Direct" },
          { name: "amount", type: "uint256", placeholder: "Enter amount", required: true, label: "Amount" }
        ],
        icon: "🎨",
        category: "User Actions"
      }
    ],

    adminFunctions: [
      {
        name: "addNewPakage",
        title: "Add New Package",
        description: "Add new package",
        inputs: [
          { name: "index", type: "uint8", placeholder: "Enter index", required: true, label: "Index" },
          { name: "newPlan", type: "tuple", placeholder: "planPrice,planLimit,holdingNft (comma separated)", required: true, label: "New Plan (comma separated)", isTuple: true },
          { name: "newTotalLevel", type: "uint8", placeholder: "Enter new total level", required: true, label: "New Total Level" }
        ],
        icon: "➕",
        category: "Admin Settings"
      },
      {
        name: "newSetUp",
        title: "New Set Up",
        description: "New set up",
        inputs: [],
        icon: "⚙️",
        category: "Admin Control"
      },
      {
        name: "pause",
        title: "Pause",
        description: "Pause contract",
        inputs: [],
        icon: "⏸️",
        category: "Admin Control"
      },
      {
        name: "unpause",
        title: "Unpause",
        description: "Unpause contract",
        inputs: [],
        icon: "▶️",
        category: "Admin Control"
      },
      {
        name: "renounceOwnership",
        title: "Renounce Ownership",
        description: "Renounce ownership",
        inputs: [],
        icon: "👑",
        category: "Admin Control"
      },
      {
        name: "transferOwnership",
        title: "Transfer Ownership",
        description: "Transfer ownership",
        inputs: [{ name: "newOwner", type: "address", placeholder: "Enter new owner", required: true, label: "New Owner" }],
        icon: "🔄",
        category: "Admin Control"
      },
      {
        name: "setIncomePercent",
        title: "Set Income Percent",
        description: "Set income percent",
        inputs: [
          { name: "newDirectOne", type: "uint256", placeholder: "Enter new direct one", required: true, label: "New Direct One" },
          { name: "newCurrLevel", type: "uint256", placeholder: "Enter new curr level", required: true, label: "New Curr Level" },
          { name: "newPlatform", type: "uint256", placeholder: "Enter new platform", required: true, label: "New Platform" },
          { name: "newBurnIncomePercent", type: "uint256", placeholder: "Enter new burn income percent", required: true, label: "New Burn Income Percent" },
          { name: "newPlatFormFeeTwoPercent", type: "uint256", placeholder: "Enter new platform fee two percent", required: true, label: "New Platform Fee Two Percent" }
        ],
        icon: "💰",
        category: "Admin Settings"
      },
      {
        name: "setNewNftMarketPlace",
        title: "Set New NFT Market Place",
        description: "Set new NFT market place",
        inputs: [{ name: "newNftMarketPlace", type: "address", placeholder: "Enter new NFT market place", required: true, label: "New NFT Market Place" }],
        icon: "🎨",
        category: "Admin Settings"
      },
      {
        name: "setLevelIncomehresholds",
        title: "Set Level Income Thresholds",
        description: "Set level income thresholds",
        inputs: [{ name: "newThresholds", type: "uint8[5]", placeholder: "Enter new thresholds (comma separated)", required: true, label: "New Thresholds", isArray: true }],
        icon: "📊",
        category: "Admin Settings"
      },
      {
        name: "setNewnHoldingNft",
        title: "Set New Holding NFT",
        description: "Set new holding NFT",
        inputs: [{ name: "newHoldingNft", type: "uint8[8]", placeholder: "Enter new holding NFT (comma separated)", required: true, label: "New Holding NFT", isArray: true }],
        icon: "🎨",
        category: "Admin Settings"
      },
      {
        name: "setNftLevelStatus",
        title: "Set NFT Level Status",
        description: "Set NFT level status",
        inputs: [
          { name: "userAccount", type: "address", placeholder: "Enter user account", required: true, label: "User Account" },
          { name: "nftPrice", type: "uint256", placeholder: "Enter NFT price", required: true, label: "NFT Price" }
        ],
        icon: "🎯",
        category: "Admin Settings"
      },
      {
        name: "setNftRewardContract",
        title: "Set NFT Reward Contract",
        description: "Set NFT reward contract",
        inputs: [{ name: "newNftReward", type: "address", placeholder: "Enter new NFT reward", required: true, label: "New NFT Reward" }],
        icon: "🏆",
        category: "Admin Settings"
      },
      {
        name: "setPlatformRewardReceiver",
        title: "Set Platform Reward Receiver",
        description: "Set platform reward receiver",
        inputs: [
          { name: "_topWallet", type: "address", placeholder: "Enter top wallet", required: true, label: "Top Wallet" },
          { name: "_newBurnFeeReceiver", type: "address", placeholder: "Enter new burn fee receiver", required: true, label: "New Burn Fee Receiver" }
        ],
        icon: "📤",
        category: "Admin Settings"
      },
      {
        name: "setPlatFormTwoReceiver",
        title: "Set Platform Two Receiver",
        description: "Set platform fee two receiver",
        inputs: [{ name: "newPlatFormFeeTwoPercent", type: "address", placeholder: "Enter new platform fee two receiver", required: true, label: "New Platform Fee Two Receiver" }],
        icon: "📤",
        category: "Admin Settings"
      },
      {
        name: "setBotStatus",
        title: "Set Bot Status",
        description: "Set bot status for an account",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" },
          { name: "status", type: "bool", placeholder: "true/false", required: true, label: "Status" }
        ],
        icon: "🤖",
        category: "Admin Settings"
      },
      {
        name: "setRewardAmount",
        title: "Set Reward Amount",
        description: "Set reward amount",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account", required: true, label: "Account" },
          { name: "amount", type: "uint256", placeholder: "Enter amount", required: true, label: "Amount" }
        ],
        icon: "💰",
        category: "Admin Settings"
      },
      {
        name: "recordPrincipleBurn",
        title: "Record Principle Burn",
        description: "Record principle burn for an account",
        inputs: [
          { name: "account", type: "address", placeholder: "Enter account address", required: true, label: "Account Address" },
          { name: "amount", type: "uint256", placeholder: "Enter amount", required: true, label: "Amount" }
        ],
        icon: "🔥",
        category: "Admin Settings"
      },
      {
        name: "setUsdt",
        title: "Set USDT",
        description: "Set USDT",
        inputs: [{ name: "newToken", type: "address", placeholder: "Enter new token", required: true, label: "New Token" }],
        icon: "💵",
        category: "Admin Settings"
      },
      {
        name: "setUserBlockRewardCondition",
        title: "Set User Block Reward Condition",
        description: "Set user block reward condition",
        inputs: [
          { name: "userAccount", type: "address", placeholder: "Enter user account", required: true, label: "User Account" },
          { name: "status", type: "bool", placeholder: "true/false", required: true, label: "Status" }
        ],
        icon: "🚫",
        category: "Admin Settings"
      },
      {
        name: "setUserExcludeRewardCondition",
        title: "Set User Exclude Reward Condition",
        description: "Set user exclude reward condition",
        inputs: [
          { name: "userAccount", type: "address", placeholder: "Enter user account", required: true, label: "User Account" },
          { name: "status", type: "bool", placeholder: "true/false", required: true, label: "Status" }
        ],
        icon: "❌",
        category: "Admin Settings"
      },
      {
        name: "setUserLevelHoldRequire",
        title: "Set User Level Hold Require",
        description: "Set user level hold require",
        inputs: [{ name: "newUserLevelHoldRequire", type: "uint256", placeholder: "Enter new user level hold require", required: true, label: "New User Level Hold Require" }],
        icon: "📋",
        category: "Admin Settings"
      },
      {
        name: "setnewSaleNftDirectThresholds",
        title: "Set New Sale NFT Direct Thresholds",
        description: "Set new sale NFT direct thresholds",
        inputs: [{ name: "newThresholds", type: "uint8[5]", placeholder: "Enter new thresholds (comma separated)", required: true, label: "New Thresholds", isArray: true }],
        icon: "📊",
        category: "Admin Settings"
      },
      {
        name: "subscribeOwner",
        title: "Subscribe Owner",
        description: "Subscribe owner",
        inputs: [
          { name: "userAccount", type: "address[]", placeholder: "Enter user accounts (comma separated)", required: true, label: "User Accounts", isArray: true },
          { name: "referrer", type: "address[]", placeholder: "Enter referrers (comma separated)", required: true, label: "Referrers", isArray: true },
          { name: "level", type: "uint8", placeholder: "Enter level", required: true, label: "Level" },
          { name: "isBlockBool", type: "bool", placeholder: "true/false", required: true, label: "Is Block Bool" }
        ],
        icon: "👑",
        category: "Admin Control"
      },
      {
        name: "botSubScribe",
        title: "Bot Subscribe",
        description: "Subscribe a bot user account",
        inputs: [{ name: "userAccount", type: "address", placeholder: "Enter user account address", required: true, label: "User Account" }],
        icon: "🤖",
        category: "Admin Control"
      },
      {
        name: "resyncOwner",
        title: "Resync Owner",
        description: "Resync owner",
        inputs: [
          { name: "startIndex", type: "uint256", placeholder: "Enter start index", required: true, label: "Start Index" },
          { name: "endIndex", type: "uint256", placeholder: "Enter end index", required: true, label: "End Index" }
        ],
        icon: "🔄",
        category: "Admin Control"
      },
      {
        name: "recoverErc20",
        title: "Recover ERC20",
        description: "Recover ERC20",
        inputs: [
          { name: "erc20Token", type: "address", placeholder: "Enter ERC20 token", required: true, label: "ERC20 Token" },
          { name: "amount", type: "uint256", placeholder: "Enter amount", required: true, label: "Amount" }
        ],
        icon: "🔄",
        category: "Admin Recovery"
      },
      {
        name: "recoverEth",
        title: "Recover Eth",
        description: "Recover Eth",
        inputs: [{ name: "amount", type: "uint256", placeholder: "Enter amount", required: true, label: "Amount" }],
        icon: "💎",
        category: "Admin Recovery"
      },
      {
        name: "setGlobalNftLimitPeriod",
        title: "Set Global NFT Limit Period",
        description: "Set global NFT limit period",
        inputs: [{ name: "newGlobalNftLimitPeriod", type: "uint256", placeholder: "Enter new global NFT limit period", required: true, label: "New Global NFT Limit Period" }],
        icon: "⏱️",
        category: "Admin Settings"
      },
      {
        name: "setHoldingMax",
        title: "Set Holding Max",
        description: "Set holding max",
        inputs: [{ name: "newHoldingMax", type: "uint256", placeholder: "Enter new holding max", required: true, label: "New Holding Max" }],
        icon: "📈",
        category: "Admin Settings"
      },
      {
        name: "initialize",
        title: "Initialize",
        description: "Initialize the contract",
        inputs: [
          { name: "initialOwner", type: "address", placeholder: "Enter initial owner address", required: true, label: "Initial Owner" },
          { name: "usdtToken", type: "address", placeholder: "Enter USDT token address", required: true, label: "USDT Token" },
          { name: "nftMarketPlaceContract", type: "address", placeholder: "Enter NFT marketplace contract", required: true, label: "NFT Marketplace Contract" }
        ],
        icon: "🔧",
        category: "Admin Control"
      }
    ]
  };

  const connectWallet = async () => {
    setLoadingStates(prev => ({ ...prev, connect: true }));
    
    try {
      const acc = await loadWeb3();
      if (acc === "No Wallet") {
        toast.error("No Wallet Detected");
        setBtnTxt("Connect Wallet");
      } else if (acc === "Wrong Network") {
        toast.error("Please switch to opBNB Mainnet");
        setBtnTxt("Wrong Network");
      } else {
        setAccount(acc);
        setBtnTxt(acc.substring(0, 6) + '...' + acc.substring(acc.length - 4));
        toast.success("Connected to opBNB Mainnet");
        
        loadMatrixData();
      }
    } catch (error) {
      console.error("Connection error:", error);
      toast.error("Connection failed");
      setBtnTxt("Connect Wallet");
    } finally {
      setLoadingStates(prev => ({ ...prev, connect: false }));
    }
  };

  const getContract = () => {
    const web3 = getWeb3Instance();
    return new web3.eth.Contract(pynexnftMatrixAbi, pynexnftMatrix);
  };

  const getUsdtContract = () => {
    const web3 = getWeb3Instance();
    return new web3.eth.Contract(USDTAbi, USDT);
  };

  const loadMatrixData = useCallback(async () => {
    if (!account) return;
    
    try {
      const web3 = getWeb3Instance();
      const contract = getContract();
      const usdtContract = getUsdtContract();
      
      const [
        totalUsers, 
        totalLevel, 
        directIncomePercent, 
        levelIncomePercent, 
        paused, 
        contractUsdtBalance, 
        userUsdtBalance
      ] = await Promise.all([
        contract.methods.totalUsers().call().catch(() => "0"),
        contract.methods.totalLevel().call().catch(() => "0"),
        contract.methods.directIncomePercent().call().catch(() => "0"),
        contract.methods.levelIncomePercent().call().catch(() => "0"),
        contract.methods.paused().call().catch(() => false),
        usdtContract.methods.balanceOf(pynexnftMatrix).call().catch(() => "0"),
        usdtContract.methods.balanceOf(account).call().catch(() => "0")
      ]);

      setMatrixData({
        totalUsers: totalUsers.toString(),
        totalLevel: totalLevel.toString(),
        directIncomePercent: directIncomePercent.toString(),
        levelIncomePercent: levelIncomePercent.toString(),
        paused: paused,
        usdtBalance: parseFloat(web3.utils.fromWei(contractUsdtBalance.toString(), 'ether')).toFixed(2),
        userUsdtBalance: parseFloat(web3.utils.fromWei(userUsdtBalance.toString(), 'ether')).toFixed(2)
      });
      
    } catch (error) {
      console.error("Error loading matrix data:", error);
      toast.error("Failed to load matrix data");
    }
  }, [account]);

  useEffect(() => {
    if (account) {
      loadMatrixData();
    }
  }, [account, loadMatrixData]);

  // Handle input changes
  const handleInputChange = (functionName, inputName, value) => {
    setFunctionInputs(prev => ({
      ...prev,
      [functionName]: {
        ...prev[functionName],
        [inputName]: value
      }
    }));
  };

  // Validate inputs based on type
  const validateInput = (type, value) => {
    if (type === 'address' && !getWeb3Instance().utils.isAddress(value)) {
      throw new Error('Invalid address');
    }
    if (type.startsWith('uint') && (isNaN(value) || Number(value) < 0)) {
      throw new Error('Invalid uint value');
    }
    if (type === 'bool' && !['true', 'false'].includes(value.toLowerCase())) {
      throw new Error('Invalid bool value');
    }
    return true;
  };

  // Execute read functions
  const executeReadFunction = async (functionData) => {
    if (!account) {
      toast.error("Please connect your wallet first");
      return;
    }

    const funcKey = functionData.name;
    setLoadingStates(prev => ({ ...prev, [funcKey]: true }));
    try {
      const contract = getContract();
      const inputs = functionInputs[funcKey] || {};
      
      const args = functionData.inputs.map(input => {
        let value = inputs[input.name] || '';
        if (input.required && value.trim() === '') {
          throw new Error(`${input.label} is required`);
        }
        if (input.isArray && typeof value === 'string') {
          value = value.split(',').map(v => v.trim());
          value.forEach(v => validateInput(input.type.replace('[]', ''), v));
          return value;
        }
        validateInput(input.type, value);
        return value;
      });

      let result = await contract.methods[functionData.name](...args).call({ from: account });

      // Handle BigInt in result
      const convertBigInt = (val) => {
        if (typeof val === 'bigint') return val.toString();
        if (typeof val === 'object' && val !== null) {
          if (Array.isArray(val)) return val.map(convertBigInt);
          const obj = {};
          Object.keys(val).forEach(key => { obj[key] = convertBigInt(val[key]); });
          return obj;
        }
        return val;
      };
      result = convertBigInt(result);

      const formattedResult = functionData.formatOutput 
        ? functionData.formatOutput(result)
        : JSON.stringify(result, (_, v) => typeof v === 'bigint' ? v.toString() : v, 2);

      setFunctionOutputs(prev => ({
        ...prev,
        [funcKey]: formattedResult
      }));

      toast.success("Read operation successful!");
    } catch (error) {
      console.error("Read error:", error);
      toast.error(error.message || "Read operation failed");
    } finally {
      setLoadingStates(prev => ({ ...prev, [funcKey]: false }));
    }
  };

  // Execute write functions with simulation and checks
  const executeWriteFunction = async (functionData) => {
    if (!account) {
      toast.error("Please connect your wallet first");
      return;
    }

    const funcKey = functionData.name;
    setLoadingStates(prev => ({ ...prev, [funcKey]: true }));
    try {
      const web3 = getWeb3Instance();
      const contract = getContract();
      const usdtContract = getUsdtContract();
      const inputs = functionInputs[funcKey] || {};
      
      const args = functionData.inputs.map(input => {
        let value = inputs[input.name] || '';
        if (input.required && value.trim() === '') {
          throw new Error(`${input.label} is required`);
        }
        if (input.isArray && typeof value === 'string') {
          return value.split(',').map(v => v.trim());
        }
        if (input.isTuple && typeof value === 'string') {
          return value.split(',').map(v => v.trim());
        }
        validateInput(input.type, value);
        if (input.type === 'bool') return value.toLowerCase() === 'true';
        return value;
      });

      // Check if paused
      const isPaused = await contract.methods.paused().call();
      if (isPaused && !['unpause'].includes(functionData.name)) {
        throw new Error('Contract is paused');
      }

      // Check if owner for admin functions
      if (functionData.category.startsWith('Admin')) {
        const owner = await contract.methods.owner().call();
        if (owner.toLowerCase() !== account.toLowerCase()) {
          throw new Error('Only owner can call this');
        }
      }

      // Balance and approval check for functions that need USDT
      if (functionData.requiresApproval || functionData.requiresBalanceCheck) {
        let requiredAmount = '0';
        if (functionData.name === 'subscribe') {
          const level = args[1];
          const plan = await contract.methods.planInfo(level).call();
          requiredAmount = plan.planPrice;
        } // Add for other functions if needed

        const userBalance = await usdtContract.methods.balanceOf(account).call();
        if (Number(userBalance) < Number(requiredAmount)) {
          throw new Error('Insufficient USDT balance');
        }

        if (functionData.requiresApproval) {
          const allowance = await usdtContract.methods.allowance(account, pynexnftMatrix).call();
          if (Number(allowance) < Number(requiredAmount)) {
            toast.info("Approving USDT...");
            const approveGas = await usdtContract.methods.approve(pynexnftMatrix, requiredAmount).estimateGas({ from: account });
            await usdtContract.methods.approve(pynexnftMatrix, requiredAmount).send({
              from: account,
              gas: Math.floor(Number(approveGas) * 1.5)
            });
            toast.success("USDT Approved!");
          }
        }
      }

      // Simulate transaction to catch revert reasons
      try {
        await contract.methods[functionData.name](...args).call({ from: account });
      } catch (simError) {
        throw new Error(`Simulation failed (would revert): ${simError.message || 'Unknown reason'}`);
      }

      // Estimate gas with buffer
      let gasEstimate;
      try {
        gasEstimate = await contract.methods[functionData.name](...args).estimateGas({ from: account });
      } catch (estError) {
        throw new Error(`Gas estimation failed: ${estError.message || 'Likely reversion'}`);
      }
      const gasWithBuffer = Math.floor(Number(gasEstimate) * 1.5);

      // Send transaction
      const tx = await contract.methods[functionData.name](...args).send({
        from: account,
        gas: gasWithBuffer
      });

      toast.success(`Transaction successful! Hash: ${tx.transactionHash}`);
      loadMatrixData();
    } catch (error) {
      console.error("Write error:", error);
      toast.error(error.message || "Write operation failed");
    } finally {
      setLoadingStates(prev => ({ ...prev, [funcKey]: false }));
    }
  };

  // Group functions by category
  const groupFunctionsByCategory = (functions) => {
    return functions.reduce((acc, func) => {
      const cat = func.category || 'Other';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(func);
      return acc;
    }, {});
  };

  // Render function card
  const renderFunctionCard = (func, isWrite = false) => {
    const funcKey = func.name;
    const inputs = functionInputs[funcKey] || {};
    const output = functionOutputs[funcKey];
    const isLoading = loadingStates[funcKey] || false;

    return (
      <div key={funcKey} className="col-lg-4 col-md-6 mb-4">
        <div className="card h-100 shadow-lg border-0" style={{
          borderRadius: '20px',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))',
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <div className="card-body p-4 d-flex flex-column">
            <div className="d-flex align-items-center mb-3">
              <span className="fs-3 me-3">{func.icon}</span>
              <div>
                <h5 className="mb-1 text-white">{func.title}</h5>
                <small className="text-white-50">{func.description}</small>
              </div>
            </div>

            {func.inputs.map(input => (
              <div key={input.name} className="mb-3">
                <label className="form-label text-white-50 small mb-1">
                  {input.label || input.name}
                  {input.required && <span className="text-danger">*</span>}
                </label>
                <input
                  type="text"
                  className="form-control bg-dark border-0 text-white placeholder-white"
                  placeholder={input.placeholder}
                  value={inputs[input.name] || ''}
                  onChange={(e) => handleInputChange(funcKey, input.name, e.target.value)}
                  style={{
                    borderRadius: '12px',
                    padding: '12px',
                    fontSize: '0.9rem'
                  }}
                />
                {(input.isArray || input.isTuple) && (
                  <small className="text-white-50 d-block mt-1">
                    Separate values with commas
                  </small>
                )}
              </div>
            ))}

            <button 
              onClick={() => isWrite 
                ? executeWriteFunction(func)
                : executeReadFunction(func)}
              disabled={isLoading}
              className="btn w-100 mt-auto"
              style={{
                background: isWrite 
                  ? 'linear-gradient(135deg, #ff6b6b, #4ecdc4)' 
                  : 'linear-gradient(135deg, #00ff88, #00ccff)',
                border: 'none',
                color: '#fff',
                borderRadius: '12px',
                padding: '12px',
                fontWeight: '600'
              }}
            >
              {isLoading ? 'Processing...' : (isWrite ? 'Execute Write' : 'Read')}
            </button>

            {output && (
              <div className="mt-3 p-3 rounded-3" style={{
                background: 'rgba(0, 255, 136, 0.1)',
                border: '1px solid rgba(0, 255, 136, 0.3)',
                whiteSpace: 'pre-wrap',
                overflow: 'auto',
                maxHeight: '200px'
              }}>
                <small className="text-white-50 d-block mb-2">Output:</small>
                <code className="text-white">{output}</code>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center p-4" style={{
      background: 'linear-gradient(135deg, #000428, #004e92)'
    }}>
      <div className="container text-center">
        <h1 className="mb-5 display-4 fw-bold" style={{
          background: 'linear-gradient(135deg, #00ff88, #00ccff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Pynex NFT Matrix Dashboard
        </h1>

        <div className="mb-5">
          <button 
            onClick={connectWallet}
            disabled={loadingStates.connect}
            className="btn px-4 px-md-5 py-3 rounded-pill shadow-lg position-relative overflow-hidden"
            style={{ 
              background: account ? 
                'linear-gradient(135deg, #00ff88, #00ccff)' : 
                'linear-gradient(135deg, #ff6b6b, #4ecdc4)',
              border: 'none',
              color: account ? '#000' : '#fff',
              fontSize: 'clamp(0.9rem, 3vw, 1.1rem)', 
              fontWeight: '700',
              transition: 'all 0.3s ease',
              minWidth: '160px',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
              opacity: loadingStates.connect ? 0.7 : 1
            }}
          >
            {loadingStates.connect ? (
              <div className="d-flex align-items-center justify-content-center">
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                Connecting...
              </div>
            ) : (
              <span className="position-relative">{btnTxt}</span>
            )}
          </button>
        </div>
        
        {account && (
          <div className="mt-4 p-3 rounded-4" style={{
            background: 'rgba(0, 255, 136, 0.1)',
            border: '1px solid rgba(0, 255, 136, 0.3)'
          }}>
            <div className="d-flex justify-content-center align-items-center gap-2">
              <span className="text-white-50 small">
                🔗 {account.substring(0, 6)}...{account.substring(account.length - 4)}
              </span>
              <span className="badge bg-success rounded-pill">Connected</span>
            </div>
          </div>
        )}

        {account && (
          <div className="row">
            <div className="col-12">
              <div className="card shadow-lg border-0 mb-4" style={{ 
                borderRadius: '24px',
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div className="card-header border-0 p-4" style={{ 
                  borderRadius: '24px 24px 0 0',
                  background: 'transparent'
                }}>
                  <ul className="nav nav-pills justify-content-center flex-wrap gap-2" role="tablist">
                    {[
                      { key: 'overview', label: 'Overview', icon: '🏪', color: '#00ff88' },
                      { key: 'read', label: 'Read Functions', icon: '👁️', color: '#45b7d1' },
                      { key: 'write', label: 'Write Functions', icon: '📝', color: '#4ecdc4' },
                      { key: 'admin', label: 'Admin Functions', icon: '⚙️', color: '#ff6b6b' }
                    ].map(tab => (
                      <li key={tab.key} className="nav-item">
                        <button
                          className={`nav-link border-0 px-4 py-3 fw-bold position-relative overflow-hidden ${
                            activeTab === tab.key ? 'text-dark' : 'text-white-50'
                          }`}
                          onClick={() => setActiveTab(tab.key)}
                          style={{
                            borderRadius: '16px',
                            background: activeTab === tab.key ? 
                              `linear-gradient(135deg, ${tab.color}, ${tab.color}80)` : 
                              'rgba(255, 255, 255, 0.05)',
                            border: activeTab === tab.key ? 
                              `1px solid ${tab.color}` : 
                              '1px solid rgba(255, 255, 255, 0.1)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            transform: activeTab === tab.key ? 'translateY(-2px)' : 'translateY(0)',
                            boxShadow: activeTab === tab.key ? 
                              `0 8px 25px ${tab.color}40` : 'none'
                          }}
                        >
                          <span className="me-2 fs-5">{tab.icon}</span>
                          <span className="d-none d-md-inline">{tab.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="card-body p-4" style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '0 0 24px 24px'
                }}>
                  {/* Overview Tab */}
                  {activeTab === 'overview' && (
                    <div className="row g-4">
                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, rgba(0, 255, 136, 0.15), rgba(69, 183, 209, 0.1))',
                          border: '1px solid rgba(0, 255, 136, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">👥</div>
                            <h4 className="text-white mb-2">{matrixData.totalUsers || '0'}</h4>
                            <p className="text-white-50 mb-0">Total Users</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={() => executeReadFunction(matrixFunctions.readFunctions.find(f => f.name === 'totalUsers'))}
                              style={{
                                background: 'rgba(0, 255, 136, 0.2)',
                                border: '1px solid #00ff88',
                                color: '#00ff88'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, rgba(69, 183, 209, 0.15), rgba(107, 224, 215, 0.1))',
                          border: '1px solid rgba(69, 183, 209, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">🏆</div>
                            <h4 className="text-white mb-2">{matrixData.totalLevel || '0'}</h4>
                            <p className="text-white-50 mb-0">Total Level</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={() => executeReadFunction(matrixFunctions.readFunctions.find(f => f.name === 'totalLevel'))}
                              style={{
                                background: 'rgba(69, 183, 209, 0.2)',
                                border: '1px solid #45b7d1',
                                color: '#45b7d1'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, rgba(107, 224, 215, 0.15), rgba(255, 170, 0, 0.1))',
                          border: '1px solid rgba(107, 224, 215, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">💰</div>
                            <h4 className="text-white mb-2">{matrixData.directIncomePercent || '0'}%</h4>
                            <p className="text-white-50 mb-0">Direct Income</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={() => executeReadFunction(matrixFunctions.readFunctions.find(f => f.name === 'directIncomePercent'))}
                              style={{
                                background: 'rgba(107, 224, 215, 0.2)',
                                border: '1px solid #6be0d7',
                                color: '#6be0d7'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: `linear-gradient(135deg, ${matrixData.paused ? 'rgba(255, 107, 107, 0.15)' : 'rgba(0, 255, 136, 0.15)'}, rgba(255, 170, 0, 0.1))`,
                          border: `1px solid ${matrixData.paused ? 'rgba(255, 107, 107, 0.3)' : 'rgba(0, 255, 136, 0.3)'}`,
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">{matrixData.paused ? '⏸️' : '▶️'}</div>
                            <h4 className="text-white mb-2">
                              {matrixData.paused ? 'PAUSED' : 'ACTIVE'}
                            </h4>
                            <p className="text-white-50 mb-0">Contract Status</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={() => executeReadFunction(matrixFunctions.readFunctions.find(f => f.name === 'paused'))}
                              style={{
                                background: matrixData.paused ? 
                                  'rgba(255, 107, 107, 0.2)' : 'rgba(0, 255, 136, 0.2)',
                                border: matrixData.paused ? 
                                  '1px solid #ff6b6b' : '1px solid #00ff88',
                                color: matrixData.paused ? '#ff6b6b' : '#00ff88'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, rgba(255, 170, 0, 0.15), rgba(255, 107, 107, 0.1))',
                          border: '1px solid rgba(255, 170, 0, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">💰</div>
                            <h4 className="text-white mb-2">{matrixData.usdtBalance || '0.00'} USDT</h4>
                            <p className="text-white-50 mb-0">Contract Balance</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={loadMatrixData}
                              style={{
                                background: 'rgba(255, 170, 0, 0.2)',
                                border: '1px solid #ffaa00',
                                color: '#ffaa00'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, rgba(0, 255, 136, 0.15), rgba(69, 183, 209, 0.1))',
                          border: '1px solid rgba(0, 255, 136, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">💼</div>
                            <h4 className="text-white mb-2">{matrixData.userUsdtBalance || '0.00'} USDT</h4>
                            <p className="text-white-50 mb-0">Your Balance</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={loadMatrixData}
                              style={{
                                background: 'rgba(0, 255, 136, 0.2)',
                                border: '1px solid #00ff88',
                                color: '#00ff88'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="card h-100 border-0" style={{ 
                          borderRadius: '20px',
                          background: 'linear-gradient(135deg, rgba(69, 183, 209, 0.15), rgba(107, 224, 215, 0.1))',
                          border: '1px solid rgba(69, 183, 209, 0.3)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div className="card-body p-4 text-center">
                            <div className="fs-1 mb-3">📊</div>
                            <h4 className="text-white mb-2">{matrixData.levelIncomePercent || '0'}%</h4>
                            <p className="text-white-50 mb-0">Level Income</p>
                            <button 
                              className="btn btn-sm mt-3 w-100"
                              onClick={() => executeReadFunction(matrixFunctions.readFunctions.find(f => f.name === 'levelIncomePercent'))}
                              style={{
                                background: 'rgba(69, 183, 209, 0.2)',
                                border: '1px solid #45b7d1',
                                color: '#45b7d1'
                              }}
                            >
                              🔄 Refresh
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Read Functions Tab */}
                  {activeTab === 'read' && (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h4 className="mb-0 text-info">👁️ Read Functions</h4>
                        <small className="text-white-50">
                          {matrixFunctions.readFunctions.length} functions available
                        </small>
                      </div>
                      {Object.entries(groupFunctionsByCategory(matrixFunctions.readFunctions)).map(([category, funcs]) => (
                        <div key={category} className="mb-5">
                          <h5 className="mb-3 text-white-50 d-flex align-items-center">
                            <span className="me-2">{category}</span>
                            <span className="badge bg-secondary rounded-pill">{funcs.length}</span>
                          </h5>
                          <div className="row">
                            {funcs.map(func => renderFunctionCard(func, false))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Write Functions Tab */}
                  {activeTab === 'write' && (
                    <div>
                      <h4 className="mb-4" style={{color: '#4ecdc4'}}>📝 Write Functions</h4>
                      {Object.entries(groupFunctionsByCategory(matrixFunctions.writeFunctions)).map(([category, funcs]) => (
                        <div key={category} className="mb-5">
                          <h5 className="mb-3 text-white-50">{category}</h5>
                          <div className="row">
                            {funcs.map(func => renderFunctionCard(func, true))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Admin Functions Tab */}
                  {activeTab === 'admin' && (
                    <div>
                      <h4 className="mb-4 text-danger">⚙️ Admin Functions</h4>
                      <div className="alert alert-warning">
                        <strong>⚠️ Warning:</strong> These functions are restricted to contract owner/admin only.
                      </div>
                      {Object.entries(groupFunctionsByCategory(matrixFunctions.adminFunctions)).map(([category, funcs]) => (
                        <div key={category} className="mb-5">
                          <h5 className="mb-3 text-white-50">{category}</h5>
                          <div className="row">
                            {funcs.map(func => renderFunctionCard(func, true))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}